import {
  ChangeDetectorRef, Component, OnInit, ViewChildren,
  OnDestroy, ViewChild, Output, EventEmitter, QueryList,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';

// data, models, helpers
import { digitalAssetsSegmentData, AssetFieldItem } from 'shared/digital-assets/digital-assets-segment-data';
import {getAvailableLanguages } from 'utils/utils';
import { isAssetItemShown, isPAG, isThirdPartyAsset, setErrors } from 'shared/digital-assets/digital-asset-helpers';
import { DigitalAsset } from '../../models/digital-asset.model';
import { segmentAnimation } from '../base/animations';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { rowData, pagRowData, photoWidthPx, collapseWidthPx } from './table-data';
import { Option } from 'shared/form/select/option.interface';
import * as dateUtils from 'utils/date';
import { thirdPartyAssetTypeLookup } from 'shared/digital-assets/digital-assets-lookups';
import { DomSanitizer } from '@angular/platform-browser';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { PrettifySizePipe } from 'shared/pipes/prettify-size.pipe';
import { AddAssetsModalComponent } from './add-assets-modal/add-assets-modal.component';
import { CollapseGroupComponent } from 'shared/collapse/collapse-group/collapse-group.component';
import { UploadAssetsComponent } from 'shared/digital-assets/upload-assets/upload-assets/upload-assets.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';

// services
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { BrandService } from 'shared/brand/brand.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import {AddUrlModalComponent} from 'shared/add-url-modal/add-url-modal.component';
import {DetailsControlComponent} from 'shared/details-page/details-control/details-control.component';


@Component({
  moduleId: module.id,
  selector: 'digital-assets-segment',
  templateUrl: 'digital-assets.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'digital-assets.component.scss'],
  animations: [segmentAnimation],
})
export class DigitalAssetsComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  @ViewChild(AddAssetsModalComponent, {static: false})
  addAssetsModal: AddAssetsModalComponent | void;

  @ViewChildren(CollapseGroupComponent)
  collapseGroups: QueryList<CollapseGroupComponent>;

  @ViewChild(UploadAssetsComponent, {static: false})
  uploadAssets: UploadAssetsComponent | void;

  @ViewChild('primaryPhotoWarningModal', {static: false})
  primaryPhotoWarningModal: SimpleConfirmModalComponent | void;

  @ViewChild('globalChangesWarningModal', {static: false})
  globalChangesWarningModal: SimpleConfirmModalComponent | void;

  @ViewChild(AddUrlModalComponent, {static: false})
  addUrlModal: AddUrlModalComponent;

  @ViewChildren('loopingControl')
  loopingControls: QueryList<DetailsControlComponent>;

  @Output() forceSave: EventEmitter<any> = new EventEmitter();

  @Output() forceCancel: EventEmitter<any> = new EventEmitter();

  collapseWidth: string = collapseWidthPx;
  photoWidth: string = photoWidthPx;
  headers: Array<any> = rowData;
  pagHeaders: Array<any> = pagRowData;

  actionItems: Array<any> = [
    {
      title: 'Add from Asset Manager',
      name: 'assign-assets'
    }
  ];
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'assets';
  segmentData: Array<AssetFieldItem> = digitalAssetsSegmentData;
  pathToData: string = 'digitalAssets';
  pathToScores: string = 'segments.digitalAssets.tallies';
  digitalAssetsSource: any;

  primaryPhotoWarningText = 'You are about to save multiple Assets as "Photo-primary"!';
  globalChangesWarningText = 'Changes to asset data are applied on all assigned products. Do you want to continue?';
  editableFieldMaps: Array<string> = [
    'resolution', 'colorMode', 'description', 'background',
    'orientation', 'orientationView', 'effectiveDate', 'expirationDate',
    'country', 'language', 'additionalInfo',
    'filesize', 'fileType', 'width', 'height', // for third party assets only?
      'frame', 'totalFrame', 'hemisphere', 'plunge', 'plane', 'totalPlanes', 'duration', 'durationUom',
    'descriptions'
  ];
  notDisplayableFields: Array<string> = ['filePath', 'uom', 'filename', 'thirdPartyAsset'];
  thirdPartyEditableFields: string[] = [
    'uri'
  ];
  selectedLanguageItem: any;
  languageItems: Array<any> = [];
  loopingFields: Array<string> = ['asc', 'des'];

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    private prettifySizePipe: PrettifySizePipe,
    private permissionsService: PermissionsService,
    protected sanitizer: DomSanitizer,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get digitalAssets(): UntypedFormArray {
    return this.segmentForm.get('digitalAssets') as UntypedFormArray;
  }

  get isMultiplePrimaryPhoto(): boolean {
    const selected: any = _.groupBy(this.digitalAssets.value, ['assetType', 'P04'])['true'];
    return selected && selected.length > 1;
  }

  get hasWarning(): boolean {
    return this.isMultiplePrimaryPhoto || this.hasGlobalChanges;
  }

  get formArray(): UntypedFormArray {
    return this.digitalAssets;
  }

  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.pathToData);
  }

  get scorecardFields(): any {
    return _.get(this.scorecardData, `segments.${this.pathToData}.fields`);
  }

  get hasPrimaryPhotoError(): boolean {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.primary-photo.valid`) === 0;
  }
  get hasPrimaryPhotoRule(): boolean {
    return _.has(this.scoreData, `segments.${this.pathToData}.non-standard.primary-photo`);
  }

  get hasAPGAssetError(): boolean {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.APG-photo.valid`) === 0;
  }
  get hasAPGAssetRule(): boolean {
    return _.has(this.scoreData, `segments.${this.pathToData}.non-standard.APG-photo`);
  }

  get hasAUDAssetError(): boolean {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.AUD-photo.valid`) === 0;
  }
  get hasAUDAssetRule(): boolean {
    return _.has(this.scoreData, `segments.${this.pathToData}.non-standard.AUD-photo`);
  }

  get hasFABAssetError(): boolean {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.FAB-photo.valid`) === 0;
  }
  get hasFABAssetRule(): boolean {
    return _.has(this.scoreData, `segments.${this.pathToData}.non-standard.FAB-photo`);
  }

  get minimumAssets(): number {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.minimum.rule`, 0);
  }

  get hasMinimumError(): boolean {
    return _.get(this.scoreData, `segments.${this.pathToData}.non-standard.minimum.valid`) === 0;
  }

  get hasDefaultPhotos(): boolean {
    return _.size(_.filter(this.itemDetailData, (item: any) => this.isMissingItem(item))) > 0;
  }

  get hasDAMPermission(): boolean {
    return this.permissionsService.hasPermission(['admin', 'dam-view', 'dam-edit', 'repository']);
  }

  get hasEditPermission(): boolean {
    return this.permissionsService.hasPermission(['admin', 'dam-edit']);
  }

  get hasGlobalChanges(): boolean {
    const dirtyAssets = this.getDirtyAssets();
    let hasGlobalChanges = false;

    _.forEach(dirtyAssets, (asset: any) => {
      _.forEach(asset.controls, (value: any, key: string) => {
        if (this.isEditable(key) && value.dirty) {
          hasGlobalChanges = true;
          return false;
        }
      });
    });

    return hasGlobalChanges;
  }

  get selectedLanguageLower(): string {
    return _.toLower(this.selectedLanguage);
  }

  get selectedLanguage(): string {
    return _.get(this.selectedLanguageItem, 'code', 'EN');
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/

  set itemDetailData(data: Array<any>) {
    _.set(this.itemDetail, this.pathToData, data);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.languageItems = getAvailableLanguages();
    this.segmentForm = this.fb.group({
      digitalAssets: this.fb.array([])
    });
    if (this.itemDetail) {
      this.handleAllDataUpdate();
    }
  }

  ngOnDestroy() {
    this.itemDetailData = this.digitalAssetsSource;
    super.ngOnDestroy();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onRowRemove(formGroup: UntypedFormGroup, index: number) {
    this.digitalAssets.removeAt(index);
    _.pullAt(this.itemDetailData, [index]);
    this.scorecardConnectorService.controlRemoved.emit({index});
    this.addMissingPhotos();
    this.markAsDirty();
  }

  onAssetsAdd(assets: Array<any>) {
    _.each(this.digitalAssets.controls, (group: UntypedFormGroup) => {
      const id = _.get(group.get('_id'), 'value');
      const asset = _.find(assets, ['_id', id]);

      if (asset && id) {
        const assetType = _.get(group.get('assetType'), 'value');
        const representation = _.get(group.get('representation'), 'value');
        _.set(asset, 'asset_type', assetType);
        _.set(asset, 'representation', representation);
      }
    });
    this.itemDetailData = _.cloneDeep(assets);
    this.setFormArray(this.segmentData, this.itemDetailData);
    this.scorecardConnectorService.controlAdded.emit({assets: this.itemDetailData, controls: this.digitalAssets});
    this.markAsDirty();
  }

  onSelectChange(option: Option, item: any) {
    if (item.item_name === 'asset_type') {
      if (option.option === 'P04' || option.option === 'AUD' || option.option === 'FAB' || option.option === 'APG') {
        const index = _.findIndex(this.itemDetailData, (_item: any) => {
          return this.isMissingItem(_item) && _item.missing_asset_type === option.option;
        });
        if (index === -1) { return; }
        this.digitalAssets.removeAt(index);
        _.defer(() => {
          _.pullAt(this.itemDetailData, [index]);
          _.pullAt(this.segmentScores, [index]);
          this.ref.markForCheck();
          this.scorecardConnectorService.detectChangesInScorecard.emit();
        });

        this.addMissingPhotos();
      } else {
        this.addMissingPhotos();
      }
    }
  }

  onWarningCancel() {
    this.forceCancel.emit();
  }

  onPrimaryPhotoWarningContinue() {
    if (this.hasGlobalChanges) {
      if (this.globalChangesWarningModal instanceof SimpleConfirmModalComponent) {
        this.globalChangesWarningModal.show();
      }
    } else {
      this.forceSave.emit();
    }
  }

  onGlobalChangesWarningContinue() {
    this.forceSave.emit();
  }

  onAddAssetClick() {
    if (this.addAssetsModal instanceof AddAssetsModalComponent) {
      this.addAssetsModal.show();
    }
  }

  onUploadAssetClick() {
    if (this.uploadAssets instanceof UploadAssetsComponent) {
      this.uploadAssets.showUploadModal();
    }
  }

  onAssetStatusChange(status: string, item: AssetFieldItem, index: number) {
    this.scorecardConnectorService.controlStatusChanged.emit({status, item, index});
  }

  onAssetPopulatedChange(populated: number, item: AssetFieldItem, index: number) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({populated, item, index});
  }

  onDateChange(selectedDate: string, item: any, formGroup: UntypedFormGroup, index: number) {
    if (item.field_map === 'expirationDate') {
      dateUtils.handleDateAheadChange(
        formGroup.get('effectiveDate'),
        'effectiveDate',
        selectedDate,
        this.segmentData,
        index
      );
    }
  }

  onAssetsUploaded(assets: any[]) {
    const digitalAssets = this.digitalAssets.value;

    _.each(assets, (asset: any) => {
      const control = _.find(digitalAssets, {'originId': asset.origin_id});

      if (control) {
        const index = _.findIndex(this.itemDetailData, {origin_id: asset.origin_id});
        // update all values that could be changed in control
        _.forEach(this.editableFieldMaps, (editableFieldMap: any) => {
          const itemName = _.get(this.getFieldByMap(editableFieldMap, this.segmentData), 'item_name');
          if (_.has(control, editableFieldMap)) {
            _.set(asset, itemName, control[editableFieldMap]);
          }
        });

        if (asset.thumbnail) {
          // update or refresh noCahce
          const noCacheIndex = asset.thumbnail.indexOf('?noCache=');
          if (noCacheIndex > 0) {
            asset.thumbnail = asset.thumbnail.substr(0, noCacheIndex);
          }
          asset.thumbnail = `${asset.thumbnail}?noCache=${Date.now()}`;
        }

        // replace this asset by the new one
        this.itemDetailData.splice(index, 1, asset);
      } else {
        this.itemDetailData.push(asset);
      }
    });

    this.itemDetailData = _.cloneDeep(this.itemDetailData);
    this.setFormArray(this.segmentData, this.itemDetailData);
    this.scorecardConnectorService.controlAdded.emit({assets: this.itemDetailData, controls: this.digitalAssets});
    this.markAsDirty();
  }

  onAddUrlClick() {
    this.addUrlModal.show();
  }

  onUrlAdd(asset) {
    const thirdPartyAsset = {
      ...asset,
      third_party_asset: 1
    };
    this.onAssetsUploaded([thirdPartyAsset]);
  }

  onPagControlValueChange(value: string, itemName: string, index: number) {
    _.set(this.itemDetailData[index], itemName, value);
    this.scorecardConnectorService.detectChangesInScorecard.emit();
    this.ref.markForCheck();
  }

  onLanguageChange(event: any) {
    this.selectedLanguageItem = event;
    this.ref.markForCheck();
  }

  onLoopingControlStatusChange(status: any, item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, item, index, language: this.selectedLanguage
    });
  }

  onLoopingControlFocus(item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlFocused.emit({
      item, index, language: this.selectedLanguage
    });
  }

  onTextPopulatedChange(populated: number, item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, item, index, language: this.selectedLanguage
    });
  }

  onDeleteControlClick(control: UntypedFormControl, item: SegmentFieldItem, language: string, i) {
    const formArray: any = this.getLoopingFormArray(item.field_map, language, i);
    if (!formArray) { return console.warn('formArray not found!'); }
    const index = formArray.controls.indexOf(control);
    formArray.removeAt(index);
    this.scorecardConnectorService.controlRemoved.emit({
      item, index, language: this.selectedLanguage,
    });
    this.markAsDirty();
  }

  onAddAnotherClick(item: SegmentFieldItem, language: string, index) {
    const validators: any = this.getFieldValidators(item);

    const formArray: any = this.getLoopingFormArray(item.field_map, language, index);
    formArray.push(new UntypedFormControl('',  validators));
    this.scorecardConnectorService.controlAdded.emit({
      item, index: formArray.length - 1, language: this.selectedLanguage,
    });
    _.defer(() => {
      this.focusOnLastLoopingControl(item.field_map);
      this.markAsDirty();
    });
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentStoredBulkData(): any {
    return this.prepareBulkData(this.itemDetailData);
  }

  public getSegmentFormData(): any {
    // filter fake missing item
    return _.filter(_.get(this, 'digitalAssets.value'), (item: any, index: number) => {
      if (item._id) { return true; }
      return !_.get(this.itemDetailData[index], 'missing');
    });
  }

  public showWarning(data?: any): void {
    // https://speedlab.atlassian.net/browse/SPD2-2263
    // https://speedlab.atlassian.net/browse/SPD2-4021
    if (this.isMultiplePrimaryPhoto) {
      if (this.primaryPhotoWarningModal instanceof SimpleConfirmModalComponent) {
        this.primaryPhotoWarningModal.show();
      }
    } else {
      if (this.globalChangesWarningModal instanceof SimpleConfirmModalComponent) {
        this.globalChangesWarningModal.show();
      }
    }
  }

  public updateScoreData(scoreData: any, scorecardData?: any): void {
    super.updateScoreData(scoreData, scorecardData);
    this.checkMissingPhotosAfterUpdate();
  }

  public getDirtyAssets(): any[] {
    if (!this.formArray) { return []; }

    return _.filter(this.formArray.controls, (digitalAssetFG: UntypedFormGroup) => {
      return digitalAssetFG.dirty;
    });
  }

  getLoopingFormArray(fieldMap, language: string, index): UntypedFormArray {
    if (!this.digitalAssets) {
      return this.fb.array([]) as UntypedFormArray;
    }
    const loopingFormArray = (this.digitalAssets.controls[index] as UntypedFormGroup).controls[fieldMap];
    return loopingFormArray as UntypedFormArray;
  }

  isLoopingFieldMasked(item: SegmentFieldItem, i: number): boolean {
    return this.hasScores ? (!this.hasItemInScores(item) && !this.hasLoopingItemInScores(item, i)) : false;
  }

  hasItemInScores(item: SegmentFieldItem): boolean {
    return _.has(this.segmentScores, `${item.item_name}.${this.selectedLanguage}`);
  }

  hasLoopingItemInScores(item: SegmentFieldItem, index: number): boolean {
    return _.has(this.segmentScores, `${item.item_name}.${this.selectedLanguage}.${index}`);
  }

  isLoopingField(item: any): boolean {
    return (item.field_map === 'descriptions');
  }

  getDescriptions(index) {
    this.notNullDescriptionsCheck(index);
    return (this.digitalAssets?.controls[index] as UntypedFormGroup)?.controls['descriptions'].value;
  }

  onChangeDescriptions($event, index) {
    (this.digitalAssets?.controls[index] as UntypedFormGroup)?.controls['descriptions'].setValue($event);
    this.markAsDirty();
  }

  notNullDescriptionsCheck(index) {
    if ((this.digitalAssets?.controls[index] as UntypedFormGroup)?.controls['descriptions'].value === null) {
      (this.digitalAssets?.controls[index] as UntypedFormGroup)?.controls['descriptions'].setValue({});
    }
  }


  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected handleAllDataUpdate() {
    super.handleAllDataUpdate();
    this.showSegment();
  }

  protected setData(itemDetail: any): void {
    this.setFormArray(this.segmentData, this.itemDetailData);
    this.digitalAssetsSource = _.cloneDeep(this.itemDetailData);
    this.addMissingPhotos();
  }

  protected updateControlsValidation(): void {
    _.each(this.itemDetailData, (asset: any, index: number) => {
      _.each(this.segmentData, (item: SegmentFieldItem) => {
        const formControl: any = this.digitalAssets.at(index).get(item.field_map);
        if (!formControl) { return; }
        const validators: any = this.getFieldValidators(item);
        this.updateControlValidators(formControl, validators);
      });
    });
  }

  /*
    * $event: {item: AssetFieldItem, index: number}
   */
  protected findControlViaData($event: any) {
    return _.nth(this.getRowControls($event.index, $event.item.field_map), 0);
  }

  /*
    * $event: {item?: AssetFieldItem, index: number, expanded: boolean}
   */
  protected handleScoreDetailsExpand($event: any): void {
    if ($event.item) {
      // focus on control
      if (!this.editMode) { return; }
      const fieldMap = $event.item.field_map;
      const control = _.head(this.getRowControls($event.index, fieldMap));
      if (control) {
        _.defer(() => control.focusControl(fieldMap));
      }
    } else {
      // expand row
      const group = this.collapseGroups.toArray()[$event.index];
      if (group && $event.expanded === group.collapsed) {
        group.toggle();
        this.ref.markForCheck();
      }
    }
  }

  protected isThirdPartyEditable(fieldMap: string, asset: any): boolean {
    return this.isThirdPartyAsset(asset) && this.thirdPartyEditableFields.indexOf(fieldMap) >= 0;
  }

  protected isEditable(fieldMap: string, asset?: UntypedFormControl): boolean {
    return this.editableFieldMaps.indexOf(fieldMap) >= 0 || this.isThirdPartyEditable(fieldMap, asset);
  }

  protected getCustomLookup(name: string, segmentData: Array<SegmentFieldItem>, formGroup: UntypedFormGroup): any {
    if (name === 'asset_type') {
      const _isThirdPartyAsset = _.get(formGroup, 'value.thirdPartyAsset') === 1;
      return _isThirdPartyAsset ? thirdPartyAssetTypeLookup : super.getLookup(name, segmentData);
    }
    return super.getLookup(name, segmentData);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private setFormArray(segmentData: Array<any>, data: any) {
    const groups = data.map((dataItem: any) => {
      return this.createFormGroup(segmentData, dataItem);
    });
    setErrors(groups, this.segmentData, this.itemDetailValidation);
    const formArray = this.fb.array(groups);
    this.segmentForm.setControl('digitalAssets', formArray);
  }

  private createFormGroup(segmentData: Array<AssetFieldItem>, dataItem: any): UntypedFormGroup {
    const model = new DigitalAsset();
    _.each(segmentData, (item: AssetFieldItem) => {
      if (!item.field_map) { return; }
      const value = _.get(dataItem, item.item_name, '');
      const validators = this.getFieldValidators(item);
      if (!value && ['asc', 'des'].includes(item.field_map)) {
        _.set(model, item.field_map, this.fb.array(['']));
        // this.buildLoopingFormArray(this.itemDetail, item, 'en', validators);
        // this.buildLoopingFormArray(this.itemDetail, item, 'es', validators);
        // this.buildLoopingFormArray(this.itemDetail, item, 'fr', validators);
        return;
      }
      _.set(model, item.field_map, new UntypedFormControl(value, validators));
    });
    return this.fb.group(model);
  }

  private getFileIconLink(asset: any): any {
    return _.get(asset, 'thumbnail');
  }

  private isFormControl(itemName: string): boolean {
    return itemName === 'asset_type' || itemName === 'representation';
  }

  private isFieldShown(item: any, data: any): boolean {
    if (_.includes(this.notDisplayableFields, item.field_map)) {
      return false;
    }
    return isAssetItemShown(data, item);
  }

  private prepareBulkData(data: any): any {
    if (!data) { return []; }
    return _.map(data, (item, index) => {
      return _.assign(item, _.mapKeys(this.digitalAssets.value[index], (value: any, key: string) => {
        return _.snakeCase(key);
      }));
    });
  }

  private hasSystemFile(asset: any): boolean {
    return !!asset.systemFilename;
  }

  private getAssetLink(asset: any): string {
    if (!this.hasDAMPermission) {
      return null;
    }
    return asset.digitalAssetId ? `/digital-asset/${asset.digitalAssetId}/details` : null;
  }

  private getAssetValue(asset: any, item: AssetFieldItem): string|number {
    const value = this.getFieldValue(asset, item);
    if (item.item_name === 'file_size') {
      return this.prettifySizePipe.transform(value);
    } else if (item.item_name === 'width' || item.item_name === 'height') {
      return (value && asset.uom) ? `${value} ${asset.uom}` : '-';
    } else {
      return value;
    }
  }

  private isPrimaryPhoto(item: any): boolean {
    return _.get(item, 'asset_type') === 'P04';
  }

  private isAUDAsset(item: any): boolean {
    return _.get(item, 'asset_type') === 'AUD';
  }

  private isAPGAsset(item: any): boolean {
    return _.get(item, 'asset_type') === 'APG';
  }

  private isFABAsset(item: any): boolean {
    return _.get(item, 'asset_type') === 'FAB';
  }

  private isMissingItem(item: any): boolean {
    return !!_.get(item, 'missing');
  }

  private hasSystemFilename(item: any): boolean {
    return !!_.get(item, 'system_filename');
  }

  private isFilenameInvalid(formGroup: UntypedFormGroup): boolean {
    const control = _.get(formGroup, 'controls.filename');
    return control.invalid;
  }

  private checkMissingPhotosAfterUpdate(): void {
    const indexes = _.pull(_.map(this.itemDetailData, (item: any, index: number) => {
      if (this.isMissingItem(item)) {
        return index;
      }
      return null;
    }), null);
    if (indexes.length) {
      _.pullAt(this.itemDetailData, indexes);
      _.each(indexes.reverse(), (index: number) => {
        this.digitalAssets.removeAt(index);
      });
    }
    _.defer(() => this.addMissingPhotos());
  }

  private addMissingPhotos(): void {

    if (this.hasPrimaryPhotoRule) {

      const primaryPhotoSize: number = _.size(_.filter(this.itemDetailData, (item: any) => {
        return item.missing_asset_type === 'P04';
      }));

      const primaryPhotoControlSize: number = _.size(_.filter(this.digitalAssets.value, (item: any) => {
        return item.assetType === 'P04';
      }));
      if (!primaryPhotoSize && !primaryPhotoControlSize) {
        const dataItem = this.createMissingPhoto('Default Photo Required', 1, 'Missing Default Photo', 'P04');
        this.itemDetailData.push(dataItem);
        this.digitalAssets.push(this.createFormGroup(this.segmentData, dataItem));
      }
    }

    if (this.hasAUDAssetRule) {
      const audAssetSize: number = _.size(_.filter(this.itemDetailData, (item: any) => {
        return item.missing_asset_type === 'AUD';
      }));
      const audAssetControlSize: number = _.size(_.filter(this.digitalAssets.value, (item: any) => {
        return item.assetType === 'AUD';
      }));
      if (!audAssetSize && !audAssetControlSize) {
        const dataItem = this.createMissingPhoto('Audio File Required', 1, 'Missing Audio File', 'AUD');
        this.itemDetailData.push(dataItem);
        this.digitalAssets.push(this.createFormGroup(this.segmentData, dataItem));
      }
    }

    if (this.hasAPGAssetRule) {
      const apgAssetSize: number = _.size(_.filter(this.itemDetailData, (item: any) => {
        return item.missing_asset_type === 'APG';
      }));
      const apgAssetControlSize: number = _.size(_.filter(this.digitalAssets.value, (item: any) => {
        return item.assetType === 'APG';
      }));
      if (!apgAssetSize && !apgAssetControlSize) {
        const dataItem = this.createMissingPhoto('Application Guide File Required', 1, 'Missing Application Guide File', 'APG');
        this.itemDetailData.push(dataItem);
        this.digitalAssets.push(this.createFormGroup(this.segmentData, dataItem));
      }
    }

    if (this.hasFABAssetRule) {
      const fabAssetSize: number = _.size(_.filter(this.itemDetailData, (item: any) => {
        return item.missing_asset_type === 'FAB';
      }));
      const fabAssetControlSize: number = _.size(_.filter(this.digitalAssets.value, (item: any) => {
        return item.assetType === 'FAB';
      }));
      if (!fabAssetSize && !fabAssetControlSize) {
        const dataItem = this.createMissingPhoto('Features and Benefits File Required', 1, 'Missing Features and Benefits File', 'FAB');
        this.itemDetailData.push(dataItem);
        this.digitalAssets.push(this.createFormGroup(this.segmentData, dataItem));
      }
    }
    // commented until we add minimum assets error
    // const missingSize: number = Math.abs(primaryPhotoSize - this.minimumAssets);
    // _.each(_.range(missingSize), (index: number) => {
    //   const dataItem = this.createMissingPhoto(index + 1, missingSize);
    //   this.itemDetailData.push(dataItem);
    //   this.digitalAssets.push(this.createFormGroup(this.segmentData, dataItem));
    // });
    this.ref.markForCheck();
  }

  private createMissingPhoto(message: string, index: number, missingTitle?: string, missingAssetType?: string, missingSize?: number): any {
    return {
      'filename': message,
      'thumbnail': '/assets/svg/missing-asset-image.svg',
      'missing': true,
      'missing_title': missingTitle,
      'missing_asset_type': missingAssetType,
      'system_filename': 'missing-asset-image.svg',
      'index': index,
      'missingSize': missingSize,
    };
  }

  private checkMissingAssets(): void {
    const primaryPhoto = _.find(this.itemDetailData, this.isPrimaryPhoto);
    const audAsset = _.find(this.itemDetailData, this.isAUDAsset);
    const apgAsset = _.find(this.itemDetailData, this.isAPGAsset);
    if ((!primaryPhoto && this.hasPrimaryPhotoError) || (!audAsset && this.hasAUDAssetError) || (!apgAsset && this.hasAPGAssetError)) {
      this.addMissingPhotos();
    }
  }


  private getInvalidDot(fieldMap: string, formGroup: UntypedFormGroup, index: number) {
    const control = _.get(formGroup, `controls.${fieldMap}`);
    if (control.invalid) {
      return   this.sanitizer.bypassSecurityTrustHtml(`
        <div class="circle-icon size-xs asset-invalid-dot circle-red"
        data-ghost="asset-invalid-dot-${fieldMap}-${index}"></div>
      `);
    } else {
      return null;
    }
  }

  private isPAG(asset: any): boolean {
    return isPAG(asset);
  }

  private isThirdPartyAsset(asset: any): boolean {
    return isThirdPartyAsset(asset);
  }

  private focusOnLastLoopingControl(fieldMap: string): void {
    _.defer(() => {
      const controls: Array<DetailsControlComponent> = this.loopingControls
        .filter((detailsControl: DetailsControlComponent) => {
          return detailsControl.fieldMap === fieldMap;
        });
      controls.pop().focusControl();
    });
  }

  private buildLoopingFormArray(itemDetail: any, item: SegmentFieldItem, language: string, validators: Array<any>) {
    const oData: any = _.get(itemDetail, item.item_name + '.' + language.toUpperCase());
    const formGroup: any = this.segmentForm.controls[language.toLowerCase()];
    let formArray: any = formGroup.get(item.field_map);
    // reset formArray if array already has data
    if (formArray.length) {
      formGroup.setControl(item.field_map, new UntypedFormArray([]));
      formArray = formGroup.get(item.field_map);
    }
    if (oData) {
      _.each(oData, (desc) => {
        formArray.push(new UntypedFormControl(desc,  validators));
      });
    } else {
      // always add one empty element to the controls array
      formArray.push(new UntypedFormControl('',  validators));
    }

  }
}
